import { useState, useEffect } from 'react'
import { toast } from '@wiggui/ds/Toast'
import { useAmplitude } from '@wiggot/analytics'
import {
  getPortalsLightCredentials,
  postPublishProperties
} from '../../../services'
import { useModalPublishPortalsErrorStore } from '../../../store'
import { createUrlMedia } from '../../../../utils'
import {
  convertToPublishPortals,
  validateAdSelectedOptions
} from '../../../utils'
import useRoles from '@wiggot/auth/hooks/useRoles'
import { delayedPublicationPortals } from '../../../constants'

const publishTexts = {
  title: 'Actualizar',
  type: 'publicar',
  portalDescription:
    'Selecciona los portales en los que quieres publicar esta propiedad. Ten en cuenta que antes de seleccionarlos debes conectar tu cuenta de cada plataforma con Wiggot, puedes hacerlo en la sección de',
  associationsDescription:
    'Selecciona las asociaciones en las que quieres publicar la propiedad.'
} as const

export const usePublishPortalErrors = () => {
  const { trackEvent } = useAmplitude()
  const { isAdminAgency, isIndependentAgent, isBasicUser } = useRoles()

  const [disabledConfirmButton, setDisabledConfirmButton] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingPublish, setIsLoadingPublish] = useState(false)
  const [step, setStep] = useState(0)

  const selectedPortalsIds = useModalPublishPortalsErrorStore(
    (state) => state?.selectedPortalsIds
  )

  const selectedAdOptions = useModalPublishPortalsErrorStore(
    (state) => state?.selectedAdOptions
  )

  const modalState = useModalPublishPortalsErrorStore(
    (state) => state?.modalState
  )

  const availablePortals = useModalPublishPortalsErrorStore(
    (state) => state?.availablePortals
  )

  const selectedAssociations = useModalPublishPortalsErrorStore(
    (state) => state?.selectedAssociations
  )

  const initialAssociations = useModalPublishPortalsErrorStore(
    (state) => state?.initialAssociations
  )

  const setAvailablePortals = useModalPublishPortalsErrorStore(
    (state) => state?.setAvailablePortals
  )

  const onSuccess = modalState?.onSuccess

  const isDisabledByOptionWithoutAd =
    validateAdSelectedOptions(selectedPortalsIds, selectedAdOptions) &&
    (isAdminAgency || isIndependentAgent || isBasicUser)

  const sortWiggotFirst = (portalId: string, beforePortalId: string) => {
    if (portalId === 'WIGGOT') {
      return -1
    }

    if (beforePortalId === 'WIGGOT') {
      return 1
    }

    if (portalId === 'MONOPOLIO') {
      return beforePortalId === 'WIGGOT' ? 1 : -1
    }

    if (beforePortalId === 'MONOPOLIO') {
      return portalId === 'WIGGOT' ? -1 : 1
    }
    return 0
  }

  const availablePortalsWithAdOptionsAndSort = availablePortals
    ?.map((portal) => {
      const logoUrl = createUrlMedia({
        id: portal?.logo
      })

      const publishIsFailed = modalState?.idFailurePortals?.includes(
        portal?.portalId
      )

      const showDelayedText = delayedPublicationPortals.includes(
        portal?.portalId
      )

      return {
        ...portal,
        showDelayedText,
        publishIsFailed,
        img: logoUrl
      }
    })
    .sort((portal, beforePortal) => {
      return sortWiggotFirst(portal?.portalId, beforePortal?.portalId)
    })

  const portals = availablePortalsWithAdOptionsAndSort?.filter(
    (portal) => !portal?.isAssociation
  )

  const resetModalState = useModalPublishPortalsErrorStore(
    (state) => state?.resetModalState
  )

  const onClose = () => {
    setStep(0)
    resetModalState()
  }

  const fetchAvailablePortals = async () => {
    try {
      setIsLoading(true)
      const { isSuccess, data } = await getPortalsLightCredentials()

      if (isSuccess && data) {
        setAvailablePortals(data)
        return
      }

      toast({
        type: 'error',
        title: 'Ocurrió un error al obtener los portales disponibles'
      })
    } catch {
      toast({
        type: 'error',
        title: 'Ocurrió un error al obtener los portales disponibles'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const generatePublishPortals = () => {
    const publishPortals = {
      ...convertToPublishPortals({
        selectedAssociations,
        selectedPortals: selectedPortalsIds,
        selectedAdOptions,
        initialAssociations
      })
    }
    return publishPortals
  }

  const backStep = () => {
    setStep(0)
  }

  const fetchPublishProperties = async () => {
    setIsLoadingPublish(true)
    try {
      const { isSuccess } = await postPublishProperties({
        propertiesIds: modalState?.propertiesIds,
        portals: generatePublishPortals()
      })

      if (isSuccess) {
        trackEvent({
          name: 'propertiesPublish_action',
          configs: {
            path: true,
            login: false
          }
        })
        toast({
          type: 'success',
          title: 'El portal se publico correctamente'
        })

        onSuccess?.(generatePublishPortals())
        onClose()
        return
      }

      toast({
        type: 'error',
        title: 'El portal no se publico correctamente'
      })
    } catch {
      toast({
        type: 'error',
        title: 'El portal no se publico correctamente'
      })
    } finally {
      setIsLoadingPublish(false)
    }
  }

  const handleOnClick = () => {
    if (step === 0) {
      setStep(1)
      return
    }
    fetchPublishProperties()
  }

  useEffect(() => {
    if (availablePortals?.length === 0) {
      fetchAvailablePortals()
    }
  }, [])

  return {
    handleOnClick,
    onClose,
    setDisabledConfirmButton,
    backStep,
    availablePortals,
    disabledConfirmButton,
    isDisabledByOptionWithoutAd,
    isLoading,
    isLoadingPublish,
    modalState,
    optionsByTabAndType: portals,
    step,
    textsByType: publishTexts,
    selectedPortalsIds
  }
}
